import { useEffect, useRef, useState } from "react";
import { X, Share, Plus, Smartphone } from "lucide-react";
import api from "@/lib/api";

const VISIT_KEY = "tsi-visit-count";
const DISMISS_KEY = "tsi-a2hs-dismissed";      // "1" = never show again
const SNOOZE_KEY = "tsi-a2hs-snooze-until";     // timestamp (ms)
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;      // 7 days

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const platform = () => (isIOS() ? "ios" : /android/i.test(navigator.userAgent) ? "android" : "other");
// Fire-and-forget install-interest analytics (anonymous unless logged in).
const track = (event) => { api.post("/analytics/a2hs", { event, platform: platform() }).catch(() => {}); };
const isStandalone = () =>
    window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

/**
 * "App coming soon! · Add to Home Screen" banner.
 * - Mobile only, and only on a REPEAT visit (visit count >= 2).
 * - Android/Chrome: uses the captured beforeinstallprompt for a real one-tap install.
 * - iOS/Safari: flips to show the Share → Add to Home Screen instructions.
 * - Dismiss options: Add to Home Screen · Maybe later (7-day snooze) · Don't show again.
 */
export default function AddToHomeScreen() {
    const [visible, setVisible] = useState(false);
    const [showIosSteps, setShowIosSteps] = useState(false);
    const deferredRef = useRef(null);

    useEffect(() => {
        // Count this visit (once per page load).
        const visits = (parseInt(localStorage.getItem(VISIT_KEY) || "0", 10) || 0) + 1;
        localStorage.setItem(VISIT_KEY, String(visits));

        const eligible = () => {
            if (isStandalone()) return false;                                   // already installed
            if (window.innerWidth >= 768) return false;                         // mobile only
            if (localStorage.getItem(DISMISS_KEY) === "1") return false;        // dismissed forever
            const snooze = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
            if (snooze && Date.now() < snooze) return false;                    // snoozed
            if (visits < 2) return false;                                       // repeat visit only
            return true;
        };

        const onBeforeInstall = (e) => {
            e.preventDefault();
            deferredRef.current = e;
            if (eligible()) setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // iOS never fires beforeinstallprompt, so show it directly after a short delay.
        let t;
        if (isIOS() && eligible()) t = setTimeout(() => setVisible(true), 2500);

        return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); if (t) clearTimeout(t); };
    }, []);

    const close = () => { setVisible(false); setShowIosSteps(false); };
    const snooze = () => { track("later"); localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); close(); };
    const never = () => { track("never"); localStorage.setItem(DISMISS_KEY, "1"); close(); };

    // Log that the prompt was actually shown (once per appearance).
    useEffect(() => { if (visible) track("shown"); }, [visible]);

    const install = async () => {
        track("install_tap");
        if (isIOS()) { setShowIosSteps(true); return; }
        const dp = deferredRef.current;
        if (dp) {
            dp.prompt();
            try { const choice = await dp.userChoice; track(choice?.outcome === "accepted" ? "installed" : "dismissed"); } catch { /* ignore */ }
            deferredRef.current = null;
            localStorage.setItem(DISMISS_KEY, "1"); // installed or declined the native dialog — don't nag again
            close();
        } else {
            setShowIosSteps(true); // fallback: show manual steps
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-x-0 bottom-0 z-[1200] p-3 sm:hidden" data-testid="a2hs-banner">
            <div className="relative mx-auto max-w-md border border-[#ff4500]/50 bg-[#0d0d0d]/95 backdrop-blur-md shadow-[0_-4px_30px_rgba(0,0,0,0.6)]">
                <button onClick={snooze} data-testid="a2hs-close" aria-label="Dismiss" className="absolute top-2 right-2 text-[#888] hover:text-white p-1">
                    <X className="h-4 w-4" />
                </button>
                <div className="p-4">
                    <div className="flex items-start gap-3">
                        <img src="/icon-192.png" alt="" className="h-11 w-11 shrink-0 rounded-lg border border-[#2a2a2a]" />
                        <div className="min-w-0 pr-5">
                            <div className="font-display text-lg tracking-wide text-white leading-none">
                                App coming soon<span className="text-[#ff4500]">!</span>
                            </div>
                            <p className="mt-1 text-xs text-[#a0a0a0] leading-snug">
                                Add TravelSports<span className="text-[#ff4500]">.</span>info to your home screen for quick access.
                            </p>
                        </div>
                    </div>

                    {showIosSteps ? (
                        <div className="mt-3 text-xs text-[#cfcfcf] leading-relaxed border-t border-[#2a2a2a] pt-3" data-testid="a2hs-ios-steps">
                            <div className="flex items-center gap-2">
                                <span>1. Tap the</span><Share className="h-4 w-4 text-[#2196f3]" /><span>Share button</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span>2. Choose</span><Plus className="h-4 w-4 text-[#2196f3]" /><span className="font-semibold text-white">Add to Home Screen</span>
                            </div>
                            <button onClick={never} data-testid="a2hs-ios-got-it" className="mt-3 w-full h-9 border border-[#2a2a2a] text-white text-xs uppercase tracking-[0.16em] hover:border-[#ff4500] transition-colors">
                                Got it
                            </button>
                        </div>
                    ) : (
                        <div className="mt-3 flex flex-col gap-2">
                            <button onClick={install} data-testid="a2hs-install" className="w-full h-10 inline-flex items-center justify-center gap-2 bg-[#ff4500] text-white text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#ff5722] transition-colors">
                                <Smartphone className="h-4 w-4" /> Add to Home Screen
                            </button>
                            <div className="flex items-center justify-between gap-2">
                                <button onClick={snooze} data-testid="a2hs-later" className="flex-1 h-9 border border-[#2a2a2a] text-[#cfcfcf] text-[11px] uppercase tracking-[0.14em] hover:border-[#555] transition-colors">
                                    Maybe later
                                </button>
                                <button onClick={never} data-testid="a2hs-never" className="flex-1 h-9 text-[#777] text-[11px] uppercase tracking-[0.14em] hover:text-[#aaa] transition-colors">
                                    Don't show again
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
